/*
 * Carbisdale - MetadataManager
 *
 * Verwaltet Dokumenten-Metadaten:
 * - Autor (global, wird für alle neuen Dokumente verwendet)
 * - Titel (pro Dokument)
 * - Timestamps (created, modified)
 * - Statistiken (Paragraphs, Zeichen)
 */

const STORAGE_KEY_AUTHOR = 'carbisdale_author'

// Format-Version fuer .cbl Dateien (muss mit FileManager.FORMAT_VERSION uebereinstimmen).
// 4.0: Outline-Redesign — drei Paragraph-Typen ('normal' / 'outline-item' / 'separator').
//      Aeltere Versionen (1.x/2.x/3.x) werden nicht mehr unterstuetzt.
export const FORMAT_VERSION = '4.0'

/**
 * MetadataManager-Klasse
 */
export class MetadataManager {
    constructor() {
        // Globaler Autor aus localStorage
        this.author = localStorage.getItem(STORAGE_KEY_AUTHOR) || ''

        // Pro-Dokument Metadaten
        this.title = ''
        this.created = null      // ISO-8601 String oder null
        this.modified = null     // ISO-8601 String oder null
    }

    // ===== Autor (global) =====

    /**
     * Gibt den globalen Autor zurück.
     * @returns {string}
     */
    getAuthor() {
        return this.author
    }

    /**
     * Setzt den globalen Autor und speichert in localStorage.
     * @param {string} author
     */
    setAuthor(author) {
        this.author = author || ''
        if (this.author) {
            localStorage.setItem(STORAGE_KEY_AUTHOR, this.author)
        } else {
            localStorage.removeItem(STORAGE_KEY_AUTHOR)
        }
    }

    // ===== Titel (pro Dokument) =====

    /**
     * Gibt den Dokumenttitel zurück.
     * @returns {string}
     */
    getTitle() {
        return this.title
    }

    /**
     * Setzt den Dokumenttitel.
     * @param {string} title
     */
    setTitle(title) {
        this.title = title || ''
    }

    // ===== Timestamps =====

    /**
     * Gibt den Erstellungszeitpunkt zurück.
     * @returns {string|null} ISO-8601 String oder null
     */
    getCreated() {
        return this.created
    }

    /**
     * Gibt den letzten Änderungszeitpunkt zurück.
     * @returns {string|null} ISO-8601 String oder null
     */
    getModified() {
        return this.modified
    }

    /**
     * Setzt den Erstellungszeitpunkt (nur wenn noch nicht gesetzt).
     */
    markCreated() {
        const now = new Date().toISOString()
        if (!this.created) {
            this.created = now
        }
        this.modified = now
    }

    /**
     * Aktualisiert den Änderungszeitpunkt.
     */
    markModified() {
        this.modified = new Date().toISOString()
    }

    /**
     * Setzt Timestamps aus geladener Datei.
     * @param {string|null} created
     * @param {string|null} modified
     */
    setTimestamps(created, modified) {
        this.created = created || null
        this.modified = modified || null
    }

    // ===== Reset =====

    /**
     * Setzt alle Dokument-Metadaten zurück (für "Neu").
     * Autor bleibt erhalten (global).
     */
    resetDocument() {
        this.title = ''
        this.created = null
        this.modified = null
    }

    /**
     * Lädt Metadaten aus einem .cbl Header.
     * @param {Object} header - Der carbisdale-Header aus der Datei
     */
    loadFromHeader(header) {
        if (header.author) {
            // Autor aus Datei übernehmen und persistieren
            this.setAuthor(header.author)
        }
        this.title = header.title || ''
        this.created = header.created || null
        this.modified = header.modified || null
    }

    /**
     * Erstellt den Header für eine .cbl Datei.
     * @returns {Object}
     */
    createHeader() {
        // Bei erstem Save: created setzen
        this.markCreated()

        return {
            formatVersion: FORMAT_VERSION,
            app: 'https://writelite.app',
            created: this.created,
            modified: this.modified,
            author: this.author || null,
            title: this.title || null
        }
    }

    // ===== Statistiken =====

    /**
     * Berechnet Dokumentstatistiken.
     * @param {EditorModel} model - Das Editor-Model
     * @returns {Object} Statistiken
     */
    getStatistics(model) {
        if (!model || !model.paragraphs) {
            return {
                paragraphs: 0,
                words: 0,
                charactersWithSpaces: 0,
                charactersWithoutSpaces: 0
            }
        }

        // Nur Main-Text zaehlen — Background-Separator + alles dahinter
        // (Story-Bible-Bereich) zaehlt nicht zur Hauptdokumentstatistik.
        const bgIdx = typeof model.getBackgroundSeparatorIndex === 'function'
            ? model.getBackgroundSeparatorIndex()
            : -1
        const cutoff = bgIdx === -1 ? model.paragraphs.length : bgIdx
        const contentParagraphs = model.paragraphs
            .slice(0, cutoff)
            .filter(p => p.type !== 'separator')
        const paragraphs = contentParagraphs.length

        // Gesamttext zusammenbauen (RT-Modus: Text ist plain, keine Marker)
        const fullText = contentParagraphs.map(p => p.text).join('\n')

        const charactersWithSpaces = fullText.length
        const charactersWithoutSpaces = fullText.replace(/\s/g, '').length

        // Woerter zaehlen (zusammenhaengende Nicht-Whitespace-Sequenzen)
        const trimmed = fullText.trim()
        const words = trimmed.length === 0 ? 0 : trimmed.split(/\s+/).length

        return {
            paragraphs,
            words,
            charactersWithSpaces,
            charactersWithoutSpaces
        }
    }

    // ===== Dateiname generieren =====

    /**
     * Generiert einen Dateinamen basierend auf Titel oder Text.
     * @param {EditorModel} model - Das Editor-Model
     * @param {string} extension - Dateiendung (z.B. 'cbl', 'md')
     * @returns {string}
     */
    generateFilename(model, extension) {
        let baseName = ''

        // 1. Priorität: Titel aus Metadaten
        if (this.title) {
            baseName = this.title
        }
        // 2. Priorität: Erste 3 Worte oder erster Absatz
        else if (model && model.paragraphs && model.paragraphs.length > 0) {
            const firstPara = model.paragraphs[0].text.trim()
            if (firstPara) {
                const words = firstPara.split(/\s+/)
                if (words.length <= 3) {
                    baseName = firstPara
                } else {
                    baseName = words.slice(0, 3).join(' ')
                }
            }
        }

        // Fallback
        if (!baseName) {
            baseName = 'Untitled'
        }

        // Blanks durch Underscore ersetzen, ungültige Zeichen entfernen
        baseName = baseName
            .replace(/\s+/g, '_')
            .replace(/[<>:"/\\|?*]/g, '')
            .substring(0, 50)  // Max 50 Zeichen

        return `${baseName}.${extension}`
    }

    // ===== YAML Frontmatter Parser =====

    /**
     * Parst YAML-Frontmatter aus Markdown-Text.
     * @param {string} text - Der Markdown-Text
     * @returns {{metadata: Object, content: string}} Metadata und bereinigter Content
     */
    parseYamlFrontmatter(text) {
        const metadata = {}
        let content = text

        // Prüfen auf YAML-Frontmatter (--- am Anfang)
        if (text.startsWith('---')) {
            const endMatch = text.substring(3).match(/\n---(\r?\n|$)/)
            if (endMatch) {
                const yamlBlock = text.substring(3, 3 + endMatch.index)
                content = text.substring(3 + endMatch.index + endMatch[0].length)

                // Einfaches YAML-Parsing (key: value)
                const lines = yamlBlock.split('\n')
                for (const line of lines) {
                    const match = line.match(/^(\w+)\s*:\s*(.*)$/)
                    if (match) {
                        const key = match[1].toLowerCase()
                        let value = match[2].trim()

                        // Anführungszeichen entfernen
                        if ((value.startsWith('"') && value.endsWith('"')) ||
                            (value.startsWith("'") && value.endsWith("'"))) {
                            value = value.slice(1, -1)
                        }

                        metadata[key] = value
                    }
                }
            }
        }

        return { metadata, content }
    }

    /**
     * Wendet geparste Frontmatter-Metadaten an.
     * @param {Object} metadata - Geparste Metadaten
     */
    applyFrontmatter(metadata) {
        if (metadata.title) {
            this.title = metadata.title
        }
        if (metadata.author) {
            this.author = metadata.author
        }
        if (metadata.date || metadata.created) {
            this.created = metadata.date || metadata.created
        }
    }

    // ===== Formatierung für Anzeige =====

    /**
     * Formatiert einen Timestamp für die Anzeige.
     * @param {string|null} isoString - ISO-8601 String
     * @returns {string} Formatierter String oder '-'
     */
    formatTimestamp(isoString) {
        if (!isoString) return '-'

        try {
            const date = new Date(isoString)
            return date.toLocaleString('de-DE', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
            })
        } catch {
            return '-'
        }
    }

    /**
     * Formatiert eine Zahl mit Tausender-Trennzeichen.
     * @param {number} num
     * @returns {string}
     */
    formatNumber(num) {
        return num.toLocaleString('de-DE')
    }
}
